/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2022 (original work) Open Assessment Technologies SA ;
 */
import { writable } from 'svelte/store';

/**
 * @type {Object} registry, for temporary storage & retrieval of multiple liveSaveStores
 */
const registry = {};

export const liveSaveStatuses = Object.freeze({
    none: 'none',
    waiting: 'waiting',
    saved: 'saved'
});

/**
 * Generate a liveSaveStore and wrap it into an API
 * @param {String} namespace - corresponds to local registry entry
 * @returns {Object} API exposing the store, and any other methods
 */
function liveSaveStoreFactory(namespace) {
    /**
     * Direct reference to store: set, subscribe
     * @type {svelte/store}
     * @property {function} set
     * @property {function} subscribe
     */
    const liveSaveStore = writable(
        {
            status: liveSaveStatuses.none,
            lastSaved: Date.now()
        },
        // eslint-disable-next-line arrow-body-style
        () => {
            // cleanup after last unsubscribe
            return () => {
                delete registry[namespace];
            };
        }
    );

    return {
        liveSaveStore,

        /**
         * Set or reset the status
         * @param {String} newStatus
         */
        reset(newStatus) {
            liveSaveStore.set({
                status: liveSaveStatuses[newStatus] || liveSaveStatuses.none,
                lastSaved: Date.now()
            });
        }
    };
}

/**
 * Public API for obtaining a store
 * @param {String} namespace - for registering a new store, or retrieving existing one
 * @returns {Object}
 */
export function getLiveSaveStore(namespace = 'livesave') {
    if (!namespace) {
        throw new TypeError('A namespace is needed to retrieve or register a liveSaveStore');
    }
    if (!registry[namespace]) {
        registry[namespace] = liveSaveStoreFactory(namespace);
    }
    return registry[namespace];
}
